import { useEffect } from 'react';

/**
 * Empêche la page de défiler (ou de zoomer) quand le doigt trace dans
 * l'élément : un pavé de signature.
 *
 * <p>Sur iPhone, la page suivait le doigt pendant la signature et le trait
 * partait de travers. Les écouteurs tactiles de React sont passifs : leur
 * `preventDefault()` est ignoré. Ceux-ci sont posés directement sur
 * l'élément, en mode non passif.
 *
 * @param {import('react').RefObject<Element>} ref élément où l'on trace
 */
export function useBloqueLeDefilement(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !el.addEventListener) return undefined;
    const bloque = (e) => {
      if (e.cancelable) e.preventDefault();
    };
    el.addEventListener('touchstart', bloque, { passive: false });
    el.addEventListener('touchmove', bloque, { passive: false });
    return () => {
      el.removeEventListener('touchstart', bloque);
      el.removeEventListener('touchmove', bloque);
    };
  }, [ref]);
}

/**
 * Garde le doigt sur le pavé jusqu'au bout du trait, même s'il en déborde
 * un instant : le trait s'arrêtait dès qu'il quittait le cadre.
 *
 * @param {PointerEvent} e appui sur le pavé
 */
export function captureLePointeur(e) {
  const el = e.currentTarget;
  if (el && el.setPointerCapture && e.pointerId != null) {
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Pointeur déjà relâché : rien à capturer.
    }
  }
}

/**
 * Les points d'un mouvement, y compris ceux que le navigateur a regroupés
 * entre deux images : sans eux, une signature rapide sortait en segments.
 *
 * @param {PointerEvent} e mouvement du pointeur
 * @returns {Array<{clientX: number, clientY: number}>} points dans l'ordre
 */
export function pointsDuMouvement(e) {
  const natif = e.nativeEvent || e;
  const groupes = natif.getCoalescedEvents ? natif.getCoalescedEvents() : [];
  return groupes && groupes.length ? groupes : [e];
}
